export type MealType = 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink';

export type FodmapRating = 'low' | 'moderate' | 'high';

export interface MealNutrition {
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  totalGrams: number;
}

export interface FoodComposition100g {
  fructoseG?: number;
  glucoseG?: number;
  excessFructoseG?: number;
  lactoseG?: number;
  sorbitolG?: number;
  mannitolG?: number;
  otherPolyolsG?: number;
  fructansG?: number;
  gosG?: number;
  totalCarbsG?: number;
  dietaryFiberG?: number;
  proteinG?: number;
  fatG?: number;
}

export type FoodProvenance = 'USDA_FDC' | 'UK_COFID' | 'EUROFIR' | 'HPLC_LITERATURE' | 'CUSTOM' | 'COMMUNITY';

/** Allergen/intolerance group an ingredient can belong to (derived at read time). */
export type ContainsGroup = 'gluten' | 'soy' | 'soy (trace)' | 'dairy';

export interface MealIngredientItem {
  id: string;
  name: string;
  canonicalName: string;
  amount: number; // e.g. 100, 1.15, 0.5
  unit: string; // 'g', 'ml', 'cup', 'tbsp', 'tsp', 'slice', 'piece', 'serving'
  gramsEstimate: number; // Normalized weight in grams
  rating: FodmapRating;
  primaryCulprit?: string; // 'Fructans', 'Mannitol', 'Sorbitol', 'Lactose', 'GOS', 'Excess Fructose'
  servingLimitNote?: string;
  compositionPer100g?: FoodComposition100g;
  provenance?: FoodProvenance;
  fodmaps?: {
    fructans: number; // 0 to 3 scale
    gos: number;
    lactose: number;
    fructose: number;
    sorbitol: number;
    mannitol: number;
  };
  calories?: number;
  protein?: number;
  fat?: number;
  carbs?: number;
  isCertifiedSwap?: boolean;
  swapSuggestion?: string;
  subIngredients?: MealIngredientItem[];
  isExpanded?: boolean;
}

export interface PlateDish {
  id: string;
  title: string;
  isCustomTitle?: boolean;
  portionMultiplier: number; // e.g. 1.0, 1.5, 0.5, 0.25
  recipeServings?: number; // e.g. 4 (if this recipe was cooked to serve 4 people)
  portionsEaten?: number; // e.g. 1 (user ate 1 of 4 servings -> 0.25x)
  ingredients: MealIngredientItem[];
  nutrition?: MealNutrition;
  notes?: string;
}

export interface CustomFoodItem {
  id: string;
  name: string;
  category: string;
  rating: FodmapRating;
  primaryCulprit?: string;
  safeGrams?: number;
  safeServingTitle?: string;
  notes?: string;
  swapSuggestion?: string;
  createdAtIso: string;
}

export interface FoodDatabaseOverride {
  id: string;
  name?: string;
  category?: string;
  rating?: FodmapRating;
  primaryCulprit?: string;
  safeGrams?: number;
  safeServingTitle?: string;
  notes?: string;
  swapSuggestion?: string;
  isHidden?: boolean;
}

export interface SavedRecipe {
  id: string;
  title: string;
  portionSize: number;
  recipeServings?: number; // e.g. 4 (serves 4 people)
  portionsEaten?: number; // e.g. 1 (user ate 1 serving)
  ingredients: MealIngredientItem[];
  nutrition?: MealNutrition;
  notes?: string;
  category?: MealType;
  createdAtIso: string;
}

export interface Meal {
  id: string;
  timestamp_iso: string;
  date: string; // YYYY-MM-DD
  meal_type: MealType;
  description: string;
  ingredients_tags: string; // Comma-separated (backward compatibility)
  ingredients?: MealIngredientItem[]; // Structured ingredients
  dishes?: PlateDish[]; // Multi-dish plate support
  portionMultiplier?: number;
  recipeServings?: number;
  portionsEaten?: number;
  nutrition?: MealNutrition;
  anti_aggravates?: string; // Comma-separated confirmed-absent allergens (e.g., 'Gluten, Dairy, Allium')
  fodmap_rating?: FodmapRating;
  stacking_report?: DetailedFodmapStackingReport;
  notes?: string;
}

export type CheckinType = 'General Daily' | 'Post-Meal' | 'Next-Morning';

export interface Symptom {
  id: string;
  timestamp_iso: string;
  date: string; // YYYY-MM-DD
  checkin_type: CheckinType;
  associated_meal_id: string | null;
  bloating_score: number; // 0-10 scale (0 = None, 10 = Severe)
  soreness_score: number; // 0-10 scale (Stomach Pain, 0 = None, 10 = Severe)
  overall_wellbeing: number; // 0-10 scale or 1-5 wellbeing
  symptom_scores?: Record<string, number>; // 0-10 scale map for all symptoms (bloating, stomach_pain, heartburn, nausea, vomiting, diarrhea, constipation, headache, eczema, hayfever, asthma)
  stool_type?: number | null; // 1-7 Bristol Stool Scale
  bad_taste_score?: number | null;
  symptom_tags: string; // Comma-separated or legacy tags
  notes?: string;
}

export type ExerciseIntensity = 'Light' | 'Moderate' | 'High';
export type DigestiveEffect = 'Relieved Bloating' | 'Improved Digestion' | 'Neutral' | 'Triggered Cramps / Discomfort';
export type ExerciseTiming = 'Post-Meal Walk' | 'Before Eating' | 'Fasted' | 'General Activity';

export interface Exercise {
  id: string;
  timestamp_iso: string;
  date: string; // YYYY-MM-DD
  activity_type: string; // 'Walking', 'Running', 'Cycling', 'Gym / Weights', 'Yoga / Stretch', 'HIIT / Cardio', 'Swimming', 'Sports', etc.
  duration_minutes: number;
  intensity: ExerciseIntensity;
  digestive_effect?: DigestiveEffect;
  timing_relative_to_meal?: ExerciseTiming;
  notes?: string;
}

export const MAIN_SYMPTOMS = [
  { id: 'bloating', name: 'Bloating', key: 'bloating', description: 'Abdominal fullness & distension' },
  { id: 'stomach_pain', name: 'Stomach Pain', key: 'stomach_pain', description: 'Cramps, spasms, visceral soreness' },
  { id: 'heartburn', name: 'Heartburn', key: 'heartburn', description: 'Acid reflux, indigestion, burning' },
  { id: 'reflux', name: 'Reflux', key: 'reflux', description: 'Acid regurgitation & throat burn' },
  { id: 'nausea', name: 'Nausea', key: 'nausea', description: 'Queasiness, sickness sensation' },
  { id: 'vomiting', name: 'Vomiting', key: 'vomiting', description: 'Emesis' },
  { id: 'diarrhea', name: 'Diarrhea', key: 'diarrhea', description: 'Loose, watery, urgent stools' },
  { id: 'constipation', name: 'Constipation', key: 'constipation', description: 'Hard stools, incomplete evacuation' },
  { id: 'headache', name: 'Headache', key: 'headache', description: 'Head pressure, migraines' },
  { id: 'mouth_taste', name: 'Mouth Taste', key: 'mouth_taste', description: '0 = Pleasant, 10 = Unpleasant' },
  { id: 'eczema', name: 'Eczema', key: 'eczema', description: 'Skin inflammation, itching, rash' },
  { id: 'hayfever', name: 'Hayfever', key: 'hayfever', description: 'Allergic rhinitis, sneezing, congestion' },
  { id: 'asthma', name: 'Asthma', key: 'asthma', description: 'Wheezing, chest tightness, shortness of breath' },
] as const;

export interface SuspectedTriggerItem {
  id: string;
  ingredient_name: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  lag_window: string; // e.g. "8–14h lag"
  transit_zone: string; // e.g. "Colonic Fermentation"
  primary_symptoms: string[];
  implicated_meals: string[];
  mechanism: string;
}

export interface SafeFoodItem {
  name: string;
  sessions_count: number;
  category: string;
}

export interface WeeklyInsight {
  generated_at_iso: string;
  week_range: string;
  ai_summary_markdown: string;
  suspected_triggers: string; // Comma-separated or JSON string
  safe_foods: string; // Comma-separated or JSON string
  health_score?: number; // 0-100 stability score
  suspected_triggers_data?: SuspectedTriggerItem[];
  safe_foods_data?: SafeFoodItem[];
  action_plans?: ActionPlanItem[];
  format_version?: 2;
  brief?: InsightBrief; // stored without timeline/olderSummary
  ai?: AiInsightResponse | null;
  verified_findings?: VerifiedFinding[];
  provider?: string;
}

export interface ActionPlanItem {
  id: string;
  title: string;
  description: string;
  category: 'elimination' | 'substitution' | 'motility' | 'reintroduction' | 'maintenance';
  target_triggers: string[]; // e.g. ['Alliums', 'Conventional Dairy', 'Gluten', 'Processed Cured Meats']
  avoid_ingredients: string[]; // e.g. ['Garlic', 'Onion', 'Milk', 'Cheese', 'Wheat', 'Bacon']
  recommended_dish?: string;
  status: 'pending' | 'accepted' | 'skipped' | 'completed';
  assigned_date?: string; // YYYY-MM-DD
}

export interface FatSecretRecipe {
  id: string;
  title: string;
  description: string;
  cooking_time_min: number;
  prep_time_min?: number;
  servings?: number;
  calories?: number;
  carbs?: number;
  protein?: number;
  fat?: number;
  image_url?: string;
  recipe_url?: string;
  avoided_triggers: string[];
  ingredients: string[];
  instructions: string[];
  source?: 'fatsecret' | 'curated';
}

export type TransitPhase = 'Acute' | 'Small Bowel' | 'Colonic' | 'Delayed Immune';

export interface CorrelationMatch {
  symptom: Symptom;
  precedingMeals: {
    meal: Meal;
    lagHours: number; // e.g. 11.5 hours
    isNextMorningLag: boolean; // 8-16h lag, typically dinner to morning
    transitPhase?: TransitPhase;
    isDelayedImmuneLag?: boolean; // 24-72h delayed reaction window
  }[];
}

export interface AppNotification {
  title: string;
  body: string;
  url?: string;
}

export type IntoleranceStatus =
  | 'Confirmed Culprit (User Verified)'
  | 'Confirmed Culprit'
  | 'Persistent Culprit'
  | 'Preliminary Suspect'
  | 'Emerging Suspect'
  | 'Eliminating'
  | 'Observing (Gathering Data)'
  | 'Tolerated (User Verified)'
  | 'Tolerated (Ruled Out by Cross-Ref)'
  | 'Tolerated'
  | 'Safe Baseline'
  | 'Watchlist';

export type ClinicalFoodCategory = 'Lactose' | 'FODMAP' | 'Gluten' | 'Histamine' | 'Protein' | 'General';

export interface UserFoodCorrection {
  foodTag: string; // Lowercase canonical tag, e.g. "cucumber", "juice", "cow's milk"
  action: 'mark_tolerated' | 'confirm_trigger' | 'blacklist_not_food' | 'alias' | 'free_from_override';
  targetAlias?: string; // e.g. "Cow's Milk"
  freeFromType?: 'gluten-free' | 'lactose-free' | 'dairy-free';
  notes?: string;
  createdAtIso: string;
}

export interface LongitudinalIntoleranceItem {
  tag: string;
  status: IntoleranceStatus;
  category?: ClinicalFoodCategory;
  expectedLagLabel?: string;
  totalTimesEaten: number;
  adverseReactionCount: number;
  reactionRate: number; // 0 - 100 percentage
  relativeRisk?: number; // e.g. 1.8x
  liftVsBaselinePercent?: number; // e.g. +35% vs baseline symptom frequency
  baselineSymptomRate?: number; // 0 - 100 percentage
  isUserVerified?: boolean;
  userCorrectionAction?: string;
  distinctTriggerDays: number;
  averageLagHours: number;
  transitPhase?: TransitPhase;
  hasDelayedReaction?: boolean; // Reacts in 24-72h window
  lastEatenDate?: string;
  daysSinceLastEaten?: number;
  eliminationClearancePercent?: number; // e.g. 35% symptom reduction during elimination
  eliminationDays?: number;
  isReintroduced?: boolean;
  reintroductionResult?: 'Tolerated' | 'Triggered Flare';
  highestSymptomScores?: { bloat: number; soreness: number };
  isExoneratedByCrossReference?: boolean;
  crossReferenceNote?: string;
  soloCalmCount?: number;
  coOccurringAggravatorCount?: number;
  swapSuggestion?: string;
}

export type TransitSpeed = 'Fast' | 'Normal' | 'Slow' | 'Sluggish';

export interface InferredConstipationPeriod {
  startDate: string;
  endDate: string;
  unloggedHours: number;
  priorMeals: Meal[];
  gapType?: 'Active Current Gap' | 'Historical Stagnation Gap';
}

export interface MotilityKineticsSummary {
  hoursSinceLastBowelMovement: number | null;
  lastBowelMovementDateStr: string | null;
  lastBowelMovementBristol: number | null;
  currentStatus: 'Regular Daily Rhythm' | 'Delayed Evacuation' | 'Active Colonic Stagnation' | 'No Data Logged';
  evacuationRhythmPerDay: number; // e.g. 1.2 bowel movements per day
  totalRecordedStools: number;
  totalLoggedDays: number;
  evacuationDaysCount: number;
  gapDaysCount: number;
  avgBloatOnEvacuationDays: number;
  avgBloatOnGapDays: number;
  bloatDeltaOnGapDays: number; // e.g. +2.4
  postEvacuationReliefAverage: number; // e.g. -2.1 points drop within 2-6h post BM
  motilityDrivenBloatPercent: number; // e.g. 68% of flares happened during a 24h+ BM gap
  bristolDistribution: {
    hardConstipationPercent: number; // Bristol 1-2
    optimalPercent: number; // Bristol 3-4
    looseUrgencyPercent: number; // Bristol 5-7
  };
  constipationPeriods: InferredConstipationPeriod[];
  clinicalRecommendation: string;
}

export interface TransitWindowConfig {
  speed: TransitSpeed;
  acuteMaxHours: number;
  smallBowelMaxHours: number;
  colonicMaxHours: number;
  delayedImmuneMaxHours: number;
  label: string;
}

export type FodmapSubtype = 'Fructans' | 'GOS' | 'Lactose' | 'Excess Fructose' | 'Sorbitol' | 'Mannitol';

export interface FodmapSubtypeLoad {
  subtype: FodmapSubtype;
  loadScore: number; // 0.0 to 3.0+ relative load
  threshold: number; // 1.0 = 100% threshold
  percentOfThreshold: number; // e.g. 75, 120
  status: 'Safe' | 'Caution' | 'Overload';
  contributingIngredients: Array<{
    name: string;
    grams: number;
    subScore: number;
    dishName?: string;
  }>;
}

export interface DetailedFodmapStackingReport {
  overallPlateRating: FodmapRating;
  isStackingWarning: boolean;
  highestCategory: FodmapSubtype;
  highestPercent: number;
  subtypes: FodmapSubtypeLoad[];
  activeWarnings: string[];
  safePortionTips: string[];
  priorMealLookback?: {
    windowHours: number;
    mealsCount: number;
    priorLoadMultiplier: number;
  };
}

export interface FodmapSubtypeCorrelation {
  subtype: FodmapSubtype;
  emoji: string;
  totalMealsCount: number;
  followedBySymptomCount: number;
  flareRatePercent: number;
  status: 'Suspected Trigger' | 'Tolerated / Ruled Out' | 'Insufficient Data';
  primarySources: string[];
  avgSeverity: number;
  clinicalNote: string;
}

export interface FodmapStackingAlert {
  isStackingWarning: boolean;
  cumulativeSubtypes: Array<{
    subtype: FodmapSubtype;
    score: number;
    threshold: number;
    sources: string[];
  }>;
  stackedCategories: string[];
  warningMessage: string;
  contributingMeals: Array<{
    mealId?: string;
    mealDescription: string;
    timeAgoHours: number;
    matchedIngredients: string[];
  }>;
}

export interface UserSession {
  userId: string;
  accountId: string;
  createdAtIso?: string;
  avatarUrl?: string;
  transitSpeed?: TransitSpeed;
  bowelFrequency?: string;
}

export type TabType = 'timeline' | 'actions' | 'insights' | 'settings';

export interface ActionGoalItem {
  id: string;
  title: string;
  description: string;
  test_meal?: string;
  status: 'pending' | 'accepted' | 'completed' | 'deferred';
  created_at_iso: string;
}

export interface ClarificationQuestion {
  id: string;
  meal_id?: string;
  meal_title?: string;
  meal_date: string;
  question: string;
  options: string[];
  answered_option?: string;
  answered_at?: string;
  ingredient_tag_map?: Record<string, string>;
}

export interface DailyBiometrics {
  date: string;
  sleep_quality?: 'Great' | 'Fair' | 'Poor';
  stress_level?: 'Low' | 'Medium' | 'High';
  avoiding_foods?: string;
  poo_logged?: boolean;
}

export interface Ticket {
  id: string;
  userId: string;
  accountId: string;
  title: string;
  description: string;
  category: 'Bug' | 'Clinical' | 'UI/Display' | 'Performance' | 'Feature';
  severity: 'Low' | 'Medium' | 'High';
  status: 'Open' | 'Investigating' | 'Resolved';
  deviceInfo?: string;
  createdAtIso: string;
  resolvedAtIso?: string;
  adminNotes?: string;
}

export interface AdminAccountSummary {
  userId: string;
  accountId: string;
  createdAtIso: string;
  mealCount: number;
  symptomCount: number;
  lastActiveIso: string | null;
  recentMeals?: Meal[];
  recentSymptoms?: Symptom[];
}

export interface AdminDashboardData {
  accounts: AdminAccountSummary[];
  aiMetrics: {
    totalCalls: number;
    byFeature: {
      describe: number;
      vision: number;
      insights: number;
      clarify: number;
      other: number;
    };
    lastCallIso?: string;
    recentCalls: Array<{
      id: string;
      feature: string;
      userId?: string;
      timestampIso: string;
      success: boolean;
      provider: string;
    }>;
  };
  tickets: Ticket[];
  stats: {
    totalAccounts: number;
    totalMeals: number;
    totalSymptoms: number;
    totalAiCalls: number;
    openTickets: number;
    resolvedTickets: number;
  };
}


/** Per-item flare statistics used by the Insight Brief (derived at read time). */
export interface ItemStat {
  name: string;
  timesEaten: number;
  timesFollowedByFlare: number;
  /** Item flare rate divided by baseline flare rate, 1 decimal place; 0 when baseline is 0. */
  lift: number;
  typicalLagHours: number | null;
  mainSymptoms: string[];
  confidence: 'low' | 'medium' | 'high';
  memberStats?: ItemStat[];
  /** Members before a stored brief capped memberStats (absent when not capped). */
  memberTotal?: number;
}

/** Gut score computed from logs (replaces the AI-invented value). */
export interface GutScore {
  score: number;
  band: 'FLARING' | 'SENSITIVE' | 'STABLE';
  loggedDays: number;
  flareDays: number;
  stableDays: number;
  components: { flareFree: number | null; severity: number | null; stool: number | null; wellbeing: number | null };
  reason: string;
}


export interface FlareEpisode {
  at: string;
  severity: number;
  symptoms: string[];
  candidateMeals: Array<{
    at: string;
    label: string;
    ingredients: string[];
    groups: ContainsGroup[];
    lagHours: number;
  }>;
}

export interface InsightBrief {
  version: 1;
  generatedAtIso: string;
  window: { from: string; to: string; daysLogged: number };
  gutScore: GutScore | null;
  previousGutScore: number | null;
  componentStats: ItemStat[];
  ingredientStats: ItemStat[];
  safeIngredients: ItemStat[];
  ruledOut: { name: string; reason: string }[];
  motility: { perDay: number; longestGapHours: number; bloatGapDelta: number };
  worstFlares: FlareEpisode[];
  changesSinceLast: {
    newSuspects: string[];
    droppedSuspects: string[];
    newlyRuledOut: string[];
    scoreDelta: number | null;
    previousPlanTitles: string[];
  } | null;
  timeline: string;
  olderSummary: string;
  /** Share (0-1) of all meals followed by a flare: the reference for every lift and overlap rate. */
  baselineFlareRate?: number;
  /** Meal counts for pairs of leads (top ingredients and eaten groups), to untangle confounders. */
  suspectOverlaps?: SuspectOverlap[];
  /** Engine-verified combinations and patterns the AI may choose from (cap 8). */
  patternCandidates?: PatternCandidate[];
  /** One deterministic line per lead summarising its overlap labels (see insightBrief.ts `computeLeadHints`). */
  leadHints?: LeadHint[];
}

export interface SuspectOverlap {
  a: string;
  b: string;
  together: number;
  togetherFlared: number;
  aWithoutB: number;
  aWithoutBFlared: number;
  bWithoutA: number;
  bWithoutAFlared: number;
  /** Engine verdict on the pair; rules in insightBrief.ts `labelOverlap`. */
  label?: OverlapLabel;
  /** For label "bystander": the item whose flares only happen alongside the other. */
  bystander?: string;
}

export interface LeadHint {
  name: string;
  hint: string;
  /** Firm engine verdict that overrides the AI's engineLeadReview verdict for this lead. */
  firm?: 'keep' | 'reject';
}

export type OverlapLabel = 'combination' | 'bystander' | 'independent' | 'inseparable';

/** Engine-verified combination or pattern, counted with the same code that verifies AI findings. */
export interface PatternCandidate {
  items: string[];
  /** Omitted for a plain combination. */
  condition?: string;
  count: number;
  flared: number;
  rate: number;
  /** Local YYYY-MM-DD days of flaring matching meals (most recent first, up to 5), usable as evidenceDates. */
  evidenceDates: string[];
  /** "weak" when count < 5 (limited evidence), else "solid". */
  strength?: 'weak' | 'solid';
}

export interface AiInsightResponse {
  headline: string;
  summary: string;
  findings: Array<{
    kind: 'ingredient' | 'group' | 'combination' | 'pattern';
    title: string;
    items: string[];
    condition?: string;
    evidenceDates: string[];
    explanation: string;
    confidence: 'low' | 'medium' | 'high';
  }>;
  engineLeadReview: Array<{ name: string; verdict: 'keep' | 'reject'; reason: string }>;
  plan: Array<{
    type: 'cut' | 'test' | 'reintroduce' | 'keep';
    title: string;
    items: string[];
    detail: string;
  }>;
  full_report: string;
}

export type VerifiedFinding = AiInsightResponse['findings'][number] & {
  verifiedCount: number;
  verifiedFlares: number;
  displayText: string;
  /** Usual flare rate (share of all meals followed by a flare) when the recount was made. */
  verifiedBaseline?: number;
};
